import { Component, computed, effect, inject, OnDestroy, OnInit, Renderer2, signal, ViewChild } from '@angular/core';
import { $state, leanPMS } from 'app/ajs-upgraded-providers';
import {
  BarcodeManagerService,
  ConfigurationManagerService,
  EntityManagerService,
  EnvironmentInfoService,
  ErrorsLoggerService,
  LoaderService,
  PaginatedResponse,
  ScreenOrientationService,
  ToolbarEventsService,
  UserActiveSessionManagerService
} from 'src/app/core';
import { AlertDialogService, ConfirmDialogService, GridDownloadDialogService, ItemLabelsDialogService, OpenDialogsService, PromptDialogService, WaitDialogService } from 'src/app/dialogs';
import { Categories, Channels, Departments, Items, Suppliers } from 'tilby-models';
import { TilbyToolbar } from 'src/app/models';
import { BarcodeComponent, GridCellFormatterService, GridFixedButtons, GridServerSideComponent, headersTranslate, ImplementServerSideGrid, transformArrayToObject } from 'src/app/shared/components';
import { AdvancedFilterModel, FilterModel, IServerSideDatasource, RowClickedEvent, RowModelType, ServerSideRowSelectionState } from 'ag-grid-community';
import { DataExport, ItemFe, QueryPagination, TableData } from 'src/app/shared/model/model';
import { delay, from, of, tap } from 'rxjs';
import { Transition } from 'angular-ui-router';
import { SharedModule } from 'src/app/shared/shared.module';
import { CommonModule } from '@angular/common';
import { CliccableActionItems, GridClickableButtonItemsComponent } from 'src/app/shared/components/grid/grid-clickable-button-items/grid-clickable-button-items.component';
import { ApplyGridFilterEvent, ExtendedTableComponent, ExtendedTableTab, Filter } from "../../../../../../shared/components/extended-table/extended-table.component";
import { TranslateService } from '@ngx-translate/core';
import { ItemDataCatalogService } from 'src/app/features/items/services/item-data-catalog.service';
import { ItemsFormService } from 'src/app/features/items/services/items-form.service';
import { ItemsToUpdate } from 'src/app/features/items/models/items-form.model';
import { GridCellRendererService } from 'src/app/shared/components/grid/utils/grid-cell-renderer.service';
import { GridSavedState, GridStateService } from 'src/app/features/items/services/grid-state.service';
import { OnDestroyService } from 'src/app/core/services/on-destroy.service';
import { subscribeInComponent } from "src/app/shared/angular-utils";
import { GeneralInputButton } from '@tilby/tilby-ui-lib/models';
import { autobind } from 'src/app/models/decorators.model';
import { GridCellColumnStyleService } from 'src/app/shared/components/grid/utils/grid-cell-column-style.service';
import { GridCellValueGetterService } from 'src/app/shared/components/grid/utils/grid-cell-value-getter.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { mobileCheck } from 'src/utilities';
import { ToolbarLayoutService } from 'src/app/shared/services/toolbar-layout.service';
import { ScaleImportDialogService } from 'src/app/dialogs/scale-import-dialog/scale-import-dialog.component';

type Scale = {
  name: string;
  type: string;
  ip_address: string;
}

@Component({
  selector: 'app-items-showcase',
  templateUrl: './items-showcase.component.html',
  styleUrl: './items-showcase.component.scss',
  standalone: true,
  imports: [
    BarcodeComponent,
    CommonModule,
    ExtendedTableComponent,
    SharedModule,
  ],
  providers: [OnDestroyService] 
})
export class ItemsShowcaseComponent implements OnInit, TilbyToolbar, ImplementServerSideGrid, OnDestroy {
  private readonly _snackBar = inject(MatSnackBar);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly barcodeManagerService = inject(BarcodeManagerService);
  private readonly configurationManagerService = inject(ConfigurationManagerService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly entityManager = inject(EntityManagerService);
  private readonly environmentInfoService = inject(EnvironmentInfoService);
  private readonly errorsLoggerService = inject(ErrorsLoggerService);
  private readonly gridCellColumnStyleService = inject(GridCellColumnStyleService);
  private readonly gridCellFormatterService = inject(GridCellFormatterService);
  private readonly gridCellRendererService = inject(GridCellRendererService);
  private readonly gridDownloadDialogService = inject(GridDownloadDialogService);
  private readonly gridStateService = inject(GridStateService);
  private readonly gridValueGetterService = inject(GridCellValueGetterService);
  private readonly itemDataCatalogService = inject(ItemDataCatalogService);
  private readonly itemLabelsDialogService = inject(ItemLabelsDialogService);
  private readonly itemsFormService = inject(ItemsFormService);
  private readonly leanPMS = inject(leanPMS);
  private readonly loaderService = inject(LoaderService);
  private readonly openDialogsService = inject(OpenDialogsService);
  private readonly promptDialogService = inject(PromptDialogService);
  private readonly renderer = inject(Renderer2);
  private readonly scaleImportDialogService = inject(ScaleImportDialogService);
  private readonly screenOrientationService = inject(ScreenOrientationService);
  private readonly state = inject($state);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
  private readonly toolbarLayoutService = inject(ToolbarLayoutService);
  private readonly translate = inject(TranslateService)
  private readonly userActiveSessionManagerService = inject(UserActiveSessionManagerService);
  private readonly waitDialogService = inject(WaitDialogService);
  
  private unregisterGlobalTransition: Function | undefined;
  private unregisterOnShowTransition: Function | undefined;

  protected isMobile = mobileCheck();
  protected showExtendedTable = signal(!this.isMobile);
  protected isLandscape = computed(() => !!this.screenOrientationService.getOrientation().includes('landscape'));
  private areEditsBlocked = this.configurationManagerService.isMagoEnabled() && this.userActiveSessionManagerService.getSession()?.role !== 'root';
  
  categoryList: Categories[];
  channelList: Channels[];
  departmentList: Departments[];
  supplierList: Suppliers[];
  itemsList = this.itemDataCatalogService.getDataByKey('items') || [];

  isClientSide: boolean = false;
  rowModelType: RowModelType = "serverSide";
  externalAgGridFilters: FilterModel | AdvancedFilterModel | null = null;
  customAgGridFilters: ApplyGridFilterEvent | null = null;
  private priceList: number = 1;
  private isFirstLoad = true;

  @ViewChild(ExtendedTableComponent) extendedTableComponent!: ExtendedTableComponent;
  @ViewChild('itemsGridComponent', {static: false}) gridRef!: GridServerSideComponent;
  readonly query: Readonly<QueryPagination> = {
    pagination: true,
    per_page: 30,
    page: 0,
  };
  private lastActiveQuery: any = null;
  datasource: IServerSideDatasource = {
    getRows: (params) => {
      if(this.isFirstLoad) {
        // this.loaderService.show("APPLICATION.LOADING_DATA");
        this.loaderService.show();
        this.isFirstLoad = false;
      }
      this.gridRef.getRowsForServersideDatasource(
        params,
        [this.query],
        (a,b?)=> {
          this.lastActiveQuery = { ...a };
          return from(this.entityManager.items.fetchCollectionOnline(a) as Promise<PaginatedResponse<Items>>).pipe(
            tap({
              next: () => this.loaderService.hide(),
              error: () => this.loaderService.hide()
            })
          );
        },
        {sort_type:"asc",sort_col:"name"}, 
        'ItemsNormalizedData',
        this.externalAgGridFilters
      )
    }
  };

  tableData: TableData[] = [];

  commonConfig: Partial<TableData> = {
    dataType: new ItemFe(),
    isCompact: true,
    headersTranslate: headersTranslate.items_showcase,
    columnsFormatter: this.gridCellFormatterService.items,
    cellComponentRenderer: this.gridCellRendererService.items,
    columnsStyle: this.gridCellColumnStyleService.items,
    // columnsExcelFormatter: this.gridCellExportStyleService.items,
    columnsValueGetter: this.gridValueGetterService.items
  };

  fixedButtons: GridFixedButtons = {
      checkbox: {visible: true, lockPosition: 'left'},
      customButton: {
        visible: true, 
        cellRenderer: GridClickableButtonItemsComponent, 
        lockPosition: 'right',
        cellRendererParams: {
          showEdit: true,
          showCopy: true,
          showLabels: true,
          showDelete: true
        }
      }
  };
  exportFormatterOptions?: (...any: any[]) => any;

  private isSelected = signal(false); 
  private isLeanSetup = signal(false);
  private movColumnsSwitch = false;
  private isAdvancedFilterActive = false;
  private scalesList: Scale[] = [];
  
  activeTabs = new Map<ExtendedTableTab, Filter[]>();
  private savedState: GridSavedState | null = null;
  
  gridStateReady = signal<ApplyGridFilterEvent | null>(null);
  
  protected gridPath = this.state.$current.name.split('.').pop() === 'showcase';

  constructor() {

    effect(() => {
      const completion = this.itemDataCatalogService.updateCompletedSignal();
      if (!completion || completion.entityName !== 'items') return;

      this.updateTable();
      this.showSnackBarMassiveUpdate();
      this.itemDataCatalogService.closeSession();
      this.loaderService.hide();
    }, { allowSignalWrites: true });

    effect(() => {
      [
        this.isLandscape(),
      ];

      setTimeout(() => {
        this.createToolbarButtons();
      }, 10)
    });

    this.unregisterGlobalTransition = this.state.router.transitionService.onSuccess({}, (transition: Transition&any) => {
      this.gridPath = transition._targetState._identifier.split('.').pop() === 'showcase'
    })

    subscribeInComponent(this.toolbarEventsService.events, (event) => {
      switch(event.type) {
        case 'toolbar.clear_search_bar':
          return this.resetSearchBar();
        case 'toolbar.search':
          this.clearExternalFilters();
          const cols = this.toolbarEventsService.searchBarXQuery['items'] || [];
          return this.gridRef.onFilterTextBoxChanged(event.search, cols);
        case 'grid.open_tool_panel':
          return this.gridRef.openToolPanel(event.panel);
        case 'toolbar.search_barcode':
          this.clearExternalFilters();
          const barcodeCols = ['barcodes.barcode', 'combinations.barcodes.barcode']
          return this.gridRef.onFilterTextBoxChanged(event.barcode, barcodeCols);
        default:
          break;
      }
    });

    // GESTIONE INIZIALIZZAZIONE AG-GRID
    if (this.itemsList.length > 100) {
      this.isClientSide = true;
      this.rowModelType = 'clientSide';
      this.tableData = [
        {
          ...this.commonConfig,
          rowData$: of(this.itemsList).pipe(delay(100)) 
        } as TableData
      ];
    } else {
      this.isClientSide = false;
      this.rowModelType = 'serverSide';
      this.tableData = [
        {
          ...this.commonConfig,
          rowData$: null
        } as TableData
      ]
    }

    this.categoryList = this.itemDataCatalogService.getExtendedCategories() ?? [];
    this.channelList = this.itemDataCatalogService.getDataByKey('channels') ?? [];
    this.departmentList = this.itemDataCatalogService.getDataByKey('departments') ?? [];
    this.supplierList = this.itemDataCatalogService.getDataByKey('suppliers') ?? [];

    this.priceList = parseInt(this.configurationManagerService.getSetting('price_list') as string, 10) || 1;

    // Fetch scales list
    try {
      const scalesList = this.configurationManagerService.getPreference('scales.list');

      if (scalesList) {
        this.scalesList = JSON.parse(scalesList);
      }

      if (!Array.isArray(this.scalesList)) {
        this.scalesList = [];
      }
    } catch {
      this.scalesList = [];
    }

    //Recupero lo stato dei filtri
    this.initFilters();
  }

  private clearExternalFilters() {
    this.toolbarEventsService.events.next({ type: 'toolbar.clear_external_filter' });
  }

  private initFilters() {
    this.savedState = this.gridStateService.getState("items") || null;

    this.customAgGridFilters = this.savedState?.customAgGridFilters || null;
    this.externalAgGridFilters = this.savedState?.customAgGridFilters?.agGridFilters || null;

    this.toolbarEventsService.searchBarValue$.next(this.savedState?.searchBarValue || '');
    this.toolbarEventsService.searchBarcode$.next(this.savedState?.searchBarcode || false);

    const initialVisibility = this.savedState?.showExtendedTable !== undefined 
        ? this.savedState.showExtendedTable 
        : !this.isMobile;

    this.showExtendedTable.set(initialVisibility);
  }

  async ngOnInit(): Promise<void> {
    const setup = await this.leanPMS.isSetup();
    this.isLeanSetup.set(setup);


    // on show again
    this.unregisterOnShowTransition = this.state.router.transitionService.onSuccess({to: 'app.new.items'}, (transition: Transition) => {
      if(!!transition.params().remove) this.gridRef.onRemoveSelected(transition.params().remove);
      this.createToolbarButtons();
    });

    this.toolbarEventsService.searchBarIcon$.next('tune');
    this.toolbarEventsService.searchBarIconFilled$.next('tune');
  }

  onGridReady() {
    // Configurazione contesto esistente
    Object.assign(this.gridRef.context, {
      ...this.gridRef.context,
      extras: { itemDataCatalogService: this.itemDataCatalogService, allData: this.isClientSide }
    })

    // Costruzione filtri aggiuntivi
    this.createCustomFilters();

    // Ripristino filtri
    if(this.savedState) {
      const api = this.gridRef.gridApi;
      if (!api || !this.savedState) return;

      const modelToRestore = { 
        ...this.savedState.filterModel, 
        ...(this.savedState.customAgGridFilters?.agGridFilters || {}) 
      };

      if (Object.keys(modelToRestore).length > 0) {
        if (this.isClientSide) {

          const promises = Object.keys(modelToRestore).map(id => api.getColumnFilterInstance(id));
          Promise.all(promises).then(() => {
            api.setFilterModel(modelToRestore);
          });
        } 
        else {
          api.setFilterModel(modelToRestore);
        }
      }

      // Ripristino ricerca esterna tramite barra di ricerca
      if(this.isClientSide && this.savedState.searchBarValue) {
        const cols = this.toolbarEventsService.searchBarXQuery['items'] || [];
        this.gridRef.onFilterTextBoxChanged(this.savedState.searchBarValue, cols);
      }

      if(this.savedState.paginationPage) {
        this.gridRef.gridApi?.paginationGoToPage(this.savedState.paginationPage);
      }

      const stateToSet = this.savedState?.customAgGridFilters || { agGridFilters: {}, activeFilterType: 'quickFilters' };
      this.gridStateReady.set(stateToSet);
    } else {
      this.gridStateReady.set({ agGridFilters: {}, activeFilterType: 'quickFilters' });
    }
  }

  onFilterChangedEmitter(event: any): void {
    if (!this.gridRef?.gridApi || this.gridRef.gridApi.isDestroyed()) {
        return;
    }

    const currentGridModel = this.gridRef.gridApi.getFilterModel();
    const updatedCustomFilters: ApplyGridFilterEvent = {
        ...(this.customAgGridFilters || { activeFilterType: 'quickFilters' }), 
        agGridFilters: this.isClientSide? currentGridModel : this.externalAgGridFilters as FilterModel
    };
    this.extendedTableComponent.updateFilterStatus(updatedCustomFilters);

    if(this.isClientSide) {
      const externalModel = this.externalAgGridFilters || {};
      const filtersAreEqual = this.deepEqual(externalModel, currentGridModel);

      if (!filtersAreEqual) {
        this.externalAgGridFilters = currentGridModel;
        this.customAgGridFilters = updatedCustomFilters;
        this.extendedTableComponent.initFiltersFromExternal(updatedCustomFilters);
      }
    }

    this.saveGridState();
    this.createToolbarButtons();
  }

  private deepEqual(obj1: any, obj2: any): boolean {
    if (obj1 === obj2) return true;

    if (obj1 == null || obj2 == null || typeof obj1 !== 'object' || typeof obj2 !== 'object') {
        return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
        if (!keys2.includes(key) || !this.deepEqual(obj1[key], obj2[key])) {
            return false;
        }
    }

    return true;
  }

  applyGridFilters(event: ApplyGridFilterEvent | null) {
    this.externalAgGridFilters = event?.agGridFilters || null;
    this.customAgGridFilters = event;

    if (!this.gridRef.gridApi) return;
    const api = this.gridRef.gridApi;
 
    setTimeout(async () => {
      if (this.isClientSide) {
        if (this.externalAgGridFilters) {
          const columnsToInit = Object.keys(this.externalAgGridFilters);
          for (const colId of columnsToInit) {
            await api?.getColumnFilterInstance(colId); 
          }
        }

        api.setFilterModel(this.externalAgGridFilters);
      } else {
        // Se c'erano filtri nativi sulla griglia, li puliamo per dare priorità a quelli custom
        if (Object.keys(api.getFilterModel()).length) {
          api.setFilterModel(null);
        }
        api.onFilterChanged();
        api.refreshServerSide({ purge: true });
      }
    })
  }

  setModuleTitle() {
    this.toolbarEventsService.moduleTitle.next("ITEMS");
  }

  async toggleFilters() {
    const newValue = !this.showExtendedTable();

    if(!newValue) {
      await this.clearGridFilters();
    } 
    this.gridStateService.updateState("items", {
      showExtendedTable: newValue
    });

    this.showExtendedTable.set(newValue);
  }
  
  private getToolbarConfig() {
    const isLeanPMSEnabled = this.isLeanSetup();
    const selectionActive = this.isSelected(); 
    const trueSignal = signal(true);
    
    const allButtons: GeneralInputButton[] = [
      {
        name: 'clear_filters',
        isIt: () => Object.keys(this.gridRef?.gridApi?.getFilterModel() || {}).length > 0,
        icon: signal('filter_alt_off'),
        label: signal('TOPBAR.ACTIONS.CLEAR_FILTERS'),
        click: () => this.clearGridFilters()
      },
      {
        name: 'add_new_items',
        isIt: trueSignal,
        icon: signal('playlist_add'),
        label: signal('TOPBAR.ACTIONS.NEW_ITEMS'),
        click: () => this.state.go("app.new.items.new")
      },
      {
        name: 'edit',
        isIt: () => selectionActive,
        icon: signal('edit'),
        label: signal('TOPBAR.ACTIONS.MASSIVE_EDIT'),
        click: () => this.openBulkEditDialog()
      },
      {
        name: 'document_scanner',
        isIt: () => selectionActive,
        icon: signal('document_scanner'),
        label: signal('TOPBAR.ACTIONS.MASSIVE_LABELS'),
        click: () => this.generateBulkLabels()
      },
      {
        name: 'delete',
        isIt: () => selectionActive,
        icon: signal('delete'),
        label: signal('TOPBAR.ACTIONS.MASSIVE_DELETE'),
        click: () => this.openDeleteDialog()
      },
      {
        name: 'search_barcode_apple',
        isIt: () => this.environmentInfoService.isAppleMobile() && !selectionActive,
        iconType: 'symbols',
        icon: signal('barcode'),
        label: signal('TOPBAR.ACTIONS.BARCODE'),
        click: () => this.onBarcodeScannedAppleMobile()
      },
      {
        name: 'search_barcode_camera',
        isIt: () => this.environmentInfoService.isCameraBarcodeSupported() && !selectionActive,
        icon: signal('photo_camera'),
        label: signal('TOPBAR.ACTIONS.PHOTO_CAMERA'),
        click: () => this.openBarcodeAndSearch()
      },
    ];

    const fixedButtons: GeneralInputButton[] = [
      {
        isIt: () => this.isMobile,
        name: 'advanced_filter',
        icon: () => this.showExtendedTable() ? 'filter_alt_off' : 'filter_list_alt',
        label: () => this.showExtendedTable() ? 'TOPBAR.ACTIONS.CUSTOM_FILTERS_DISABLED' : 'TOPBAR.ACTIONS.CUSTOM_FILTERS_ENABLED',
        click: () => this.toggleFilters()
      },
      {
        name: 'columns_panel',
        isIt: trueSignal,
        icon: signal('view_week'),
        label: signal('TOPBAR.ACTIONS.COLUMN_SELECTED'),
        click: () => this.gridRef.openToolPanel('columns')
      },
      {
        isIt: trueSignal,
        name: 'movable_columns',
        icon: signal('drag_indicator'),
        label: () => this.movColumnsSwitch ? 'TOPBAR.ACTIONS.DISABLED_COLUMN_MOVEMENT': 'TOPBAR.ACTIONS.ENABLED_COLUMN_MOVEMENT',
        click: () => this.gridRef.columnMovableSwitch(this.movColumnsSwitch = !this.movColumnsSwitch)
      },
      {
        isIt: trueSignal,
        name: 'save_columns_position',
        icon: signal('save'),
        label: signal('TOPBAR.ACTIONS.SAVE_COLUMN_PREFERENCE'),
        click: () => this.gridRef.openDialogSaveColumnsPreference()
      },
      {
        isIt: () => !this.isMobile,
        name: 'advanced_filter',
        icon: () => this.isAdvancedFilterActive ? 'filter_alt_off' : 'filter_list_alt',
        label: () => this.isAdvancedFilterActive ? 'TOPBAR.ACTIONS.ADVANCED_FILTERS_DISABLED' : 'TOPBAR.ACTIONS.ADVANCED_FILTERS_ENABLED',
        click: () => this.gridRef.advancedFilterSwitch(this.isAdvancedFilterActive = !this.isAdvancedFilterActive)
      },
      {
        isIt: trueSignal,
        name: 'upload',
        icon: signal('upload'),
        label: signal('TOPBAR.ACTIONS.IMPORT'),
        click: () => this.goToImporter()
      },
      {
        isIt: signal(isLeanPMSEnabled),
        name: 'upload_from_lean',
        icon: signal('upload'),
        label: signal('ITEMS.TOPBAR_ITEMS.LEAN_PMS_IMPORT'),
        click: () => this.onLeanPmsImport()
      },
      {
        isIt: signal(this.scalesList.length > 0),
        name: 'import_scales',
        icon: signal('scale'),
        label: signal('ITEMS.TOPBAR_ITEMS.IMPORT_SCALES'),
        click: () => this.onImportScales()
      },
      {
        isIt: trueSignal,
        name: 'update',
        icon: signal('update'),
        label: signal('ITEMS.TOPBAR_ITEMS.UPDATE'),
        click: () => this.updateTable()
      },
      {
        name: 'export',
        isIt: () => true,
        icon: signal('download'),
        label: signal('TOPBAR.ACTIONS.EXPORT'),
        click: () => this.triggerExport()
      },
      {
        isIt: trueSignal,
        name: 'settings_backup_restore',
        icon: signal('settings_backup_restore'),
        label: signal('TOPBAR.ACTIONS.RESET_TABLE'),
        click: () => this.gridRef.resetLayout()
      }
    ];

    return { allButtons, fixedButtons };
  }

  createToolbarButtons() {
    const { allButtons, fixedButtons } = this.getToolbarConfig();
    this.toolbarLayoutService.updateToolbarLayout(allButtons, fixedButtons);
  }

  createCustomFilters() {
    let customFilters: Filter[] = [];
    let quickFilters: Filter[] = [];
    
    const categoryNameColumnDef = this.gridRef.gridApi?.getColumnDef("category_name")
    if(categoryNameColumnDef && categoryNameColumnDef.field) {
      customFilters.push({
        name: this.translate.instant(this.tableData[0].headersTranslate.get("category_name") || ''),
        field: categoryNameColumnDef.field,
        filter: categoryNameColumnDef.filter,
        filterParams: categoryNameColumnDef.filterParams,
        options: this.categoryList
      });
      quickFilters.push({
        name: this.translate.instant(this.tableData[0].headersTranslate.get("category_name") || ''),
        field: categoryNameColumnDef.field,
        filter: categoryNameColumnDef.filter,
        filterParams: categoryNameColumnDef.filterParams,
        options: this.categoryList
      });
    }

    const departmentNameColumnDef = this.gridRef.gridApi?.getColumnDef("department_name");
    let headerIdentifier = this.tableData[0].headersTranslate.get("department_name")!;
    const key = headerIdentifier.split('|')[0];
    const interpolateParams = headerIdentifier.split('|').length > 1? transformArrayToObject(headerIdentifier.split('|')[1].split(',')): undefined;
    if(departmentNameColumnDef && departmentNameColumnDef.field) {
      customFilters.push({
        name: this.translate.instant(key, interpolateParams),
        field: departmentNameColumnDef.field,
        filter: departmentNameColumnDef.filter,
        filterParams: departmentNameColumnDef.filterParams,
        options: this.departmentList
      });
      quickFilters.push({
        name: this.translate.instant(key, interpolateParams),
        field: departmentNameColumnDef.field,
        filter: departmentNameColumnDef.filter,
        filterParams: departmentNameColumnDef.filterParams,
        options: this.departmentList
      });
    }

    this.activeTabs.set("customFilters", customFilters);
    this.activeTabs.set("quickFilters", quickFilters);
  }

  async clearGridFilters() {
    const api = this.gridRef.gridApi;
    if (!api) return;

    const currentModel = api.getFilterModel();
    api.setFilterModel(null);

    // Se siamo in client-side, forziamo la pulizia delle istanze
    if (this.isClientSide) {
      Object.keys(currentModel).forEach(async (colId) => {
        const filterInstance = await api.getColumnFilterInstance(colId);
        if (filterInstance) filterInstance.setModel(null);
      });
    }
  }

  @autobind
  async onExport(dataExport: DataExport) {
    // LOGICA IBRIDA PER EXPORT
    if (this.gridRef.gridApi?.getGridOption('rowModelType') === 'serverSide') {
      const rowsToExport = await this.getSelectedRows();

      if (!rowsToExport || rowsToExport.length === 0) {
        return;
      }

      this.gridRef.massiveExportFromLocalStorage(dataExport, rowsToExport);
    } else {
      const rowSelected = !!this.gridRef.getSelectedCount();
      this.gridRef?.clientSideExport(dataExport, rowSelected);
    }
  }
  
  goToImporter() {
    this.state.go('app.new.file_importer');
  }
  
  /**
  * Gestisce l'importazione LeanPMS
  */
  async onLeanPmsImport() {
    const confirmed = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'ITEMS.LEAN_PMS_IMPORT.CONFIRM_IMPORT',
        confirmLabel: 'APPLICATION.YES',
        cancelLabel: 'APPLICATION.NO'
      }
    });

    if (!confirmed) return;

    const importPromise = new Promise(async (resolve) => {
      try {
        await this.leanPMS.importItems();
        resolve(null);
        this.alertDialogService.openDialog({
          data: { messageLabel: this.translate.instant('ITEMS.LEAN_PMS_IMPORT.IMPORT_COMPLETE') }
        });
      } catch (error: any) {
        resolve(error);
        this.handleLeanPmsError(error);
      } finally {
        this.gridRef.gridApi?.refreshServerSide({ purge: true });
      }
    });

    await this.waitDialogService.openDialog({
      data: { 
        message: 'ITEMS.LEAN_PMS_IMPORT.IMPORTING', 
        promise: importPromise 
      }
    });
  }

  async onImportScales() {
    this.scaleImportDialogService.openDialog({
      data: {
        scales: this.scalesList
      }
    });
  }

  private handleLeanPmsError(error: any) {
    if (error && error.message && typeof error === 'object') {
      const translatedMessage = this.translate.instant(
        'ITEMS.' + error.message, 
        error.errorInfo
      );
      this.alertDialogService.openDialog({
        data: { messageLabel: translatedMessage }
      });
    } else {
      this.alertDialogService.openDialog({
        data: { messageLabel: 'ITEMS.LEAN_PMS_IMPORT.UNKNOWN_ERROR' }
      });
    }
  }

  updateTable() {
    if(!this.isClientSide) {
      this.gridRef.gridApi?.refreshServerSide({ purge: true });
    } else {
      this.tableData[0].rowData$ = of(this.itemDataCatalogService.getDataByKey('items') || []).pipe(delay(100));
    }
  }

  async triggerExport() {
    const res = await this.gridDownloadDialogService.openDialog('items', {
      data: this.tableData.map(tableData => ({
        ...tableData,
        sortFilterQuery: this.gridRef.getSortFilterQuery(),
        countSelectedRows: this.gridRef.getSelectedCount() || this.gridRef.gridApi?.getDisplayedRowCount()
      }))
    });

    if (res) {
      this.onExport(res);
    }
  }

  @autobind
  async openBulkEditDialog() {
    if(this.areEditsBlocked) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('ITEMS.NO_EDIT_WITH_MAGO_ENABLED')
        }})
      return;
    }
    
    const result = await this.openDialogsService.openEditRowsDialog<Partial<ItemsToUpdate>>({
      data: {
        form: this.itemsFormService.createBulkEditForm()
      }
    });

    if (result) {
      this.updateItems(result);
    }
  }

  async getSelectedRows() {
    const api = this.gridRef?.gridApi;
    if (!api) return [];

    if (this.isClientSide) {
      return api.getSelectedRows() || [];
    }

    const gridState = api.getState();
    const selectionState = gridState.rowSelection as ServerSideRowSelectionState;

    const isSelectAllActive = selectionState?.selectAll === true;
    const toggledNodes = selectionState?.toggledNodes || [];
    const hasToggledNodes = toggledNodes.length > 0;

    // Selezione Manuale (isSelectAllActive è false, ma ci sono nodi selezionati)
    if (!isSelectAllActive && hasToggledNodes) {
      const allData = this.itemDataCatalogService.getDataByKey("items") || [];
      return allData.filter(item => toggledNodes.includes(item.uuid || ''));
    }
    // "Seleziona Tutto" o "Nessuna selezione" (ma con filtri attivi)
    if (isSelectAllActive || (!isSelectAllActive && !hasToggledNodes)) {
      try {
        this.loaderService.show();

        const requestQuery = {
          ...(this.lastActiveQuery || {}),
          pagination: false,
          per_page: undefined,
          page: undefined
        };

        const response = await this.entityManager.items.fetchCollectionOnline(requestQuery) as Items[];
        let results = response || [];

        if (isSelectAllActive && hasToggledNodes) {
          results = results.filter(item => !toggledNodes.includes(item.uuid || ''));
        }

        return results;
      } catch (error) {
        console.error("Errore nel recupero massivo per modifica:", error);
        return [];
      } finally {
        this.loaderService.hide();
      }
    }
    return [];
  }

  @autobind
  async updateItems(updatedFields: Partial<ItemsToUpdate>) {
    const selectedRows = await this.getSelectedRows();

    if (selectedRows.length === 0) {
      return;
    }

    const fieldsToUpdate = Object.entries(updatedFields).reduce((acc, [key, value]) => {
      if (value === undefined) return acc;

      let finalValue: any = value;                                                                                                     

      if (key === 'stock_type') {
        finalValue = value ? 'simple' : null;
      } 
      
      else if ((key.startsWith('department') && key.endsWith('_id')) || key === 'category_id' || key === 'color') {
        finalValue = (value === 0 || value === '0') ? null : value;
      }
      acc[key as keyof ItemsToUpdate] = finalValue;
      return acc;
    }, {} as Partial<ItemsToUpdate>);

    if (Object.keys(fieldsToUpdate).length === 0) {
      return;
    }

    const itemsToUpdate: ItemsToUpdate[] = selectedRows.map(row => ({
      uuid: row.uuid || '',
      ...fieldsToUpdate
    }));

    this.itemDataCatalogService.startUpdateSession('items', selectedRows.length);
    this.gridRef.idsToRestore = selectedRows.map(row => row.uuid || '');

    try {
      this.loaderService.show();
      await this.entityManager.items.bulkUpdateOnline(itemsToUpdate);
    } catch (error) {
      this.errorsLoggerService.err(error);
      this.gridRef.idsToRestore = [];
      this.itemDataCatalogService.startUpdateSession('items', 0);
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('ITEMS.SHOWCASE.BULK_EDIT_ERROR')
        }
      });
    } finally {
      this.loaderService.hide();
    }
  }

  showSnackBarMassiveUpdate() {
    this._snackBar.open(this.translate.instant(`ITEMS.SHOWCASE.BULK_EDIT_SUCCESS`), undefined, { 
      duration: 3000, 
      horizontalPosition: 'start', 
      verticalPosition: "bottom", 
    });
  }

  async openDeleteDialog(id?: number, data?: Items) {
    if(this.areEditsBlocked) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('ITEMS.NO_EDIT_WITH_MAGO_ENABLED')
        }})
      return;
    }

    let itemsToDelete: any[] = [];

    if(id) {
      itemsToDelete = [{ id: id }];
    } else {
      itemsToDelete = this.gridRef.gridApi?.getSelectedRows() || [];
    }

    if(itemsToDelete.length === 0) return;

    let message = this.translate.instant('ITEMS.SHOWCASE.WANT_TO_DELETE_TITLE');
    if(id && data?.combinations?.length !== 0) {
      message += this.translate.instant('ITEMS.SHOWCASE.WANT_TO_DELETE_DESCRIPTION', {value: data?.combinations?.length})
    }
    const confirm = await this.confirmDialogService.openDialog({
        data: {
            messageLabel: message
        }
    });

    if(!confirm) return;

    this.gridRef.onRemoveSelected(id);
  }

  actions({ action, data }: CliccableActionItems<Items>) {
    if(data) {
      switch (action) {
        case "details": 
          return this.goToDetails(data);
        case "copy": 
          return this.cloneItem(data);
        case "generate-labels": 
          return this.generateLabels(data);
        case "delete": 
          return this.openDeleteDialog(data.id, data);
      }
    }
  }

  rowClicked(params: RowClickedEvent) {
    // this.movColumnsSwitch && this.gridRef.columnMovableSwitch(this.movColumnsSwitch = false);
    if (this.state.router.globals.transition) {
      return; 
    }
    this.goToDetails(params.data);
  }

  goToDetails(row: Items) {
    if(!row || !row.id) return;
    this.state.go('app.new.items.details', { itemId: row.id });
    this.loaderService.show();
  }

  /**
   * Duplica un prodotto esistente chiedendo un nuovo nome
   * @param item L'oggetto Item originale da clonare
   */
  async cloneItem(item: Items) {
    if(this.areEditsBlocked) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('ITEMS.NO_EDIT_WITH_MAGO_ENABLED')
        }})
      return;
    }

    const newName = await this.promptDialogService.openDialog({
      data: {
        title: this.translate.instant('ITEMS.SHOWCASE.COPY_TITLE'),
        value: this.translate.instant('ITEMS.SHOWCASE.COPY_TEXT', { value: item.name }).substring(0, 255), minLength: 0, maxLength: 255
    }}
  );
  if (!newName) return;

  try {
      const {
        barcodes,
        category,
        code_pienissimo,
        combinations,
        id,
        index,
        name,
        option1_value,
        option2_value,
        option3_value,
        option4_value,
        sku,
        uuid,
        ...rest
      } = item;

      // Clone profondo dell'oggetto
      let clonedItem = JSON.parse(JSON.stringify(rest));

      // Pulizia ricorsiva degli ID e metadati
      this.recursiveCleanup(clonedItem);

      // Assegnazione nuovi valori e ripristino componenti (solo ID)
      Object.assign(clonedItem, {
          name: newName,
          barcodes: [],
          combinations: [],
          allergens: (item.allergens || []).map(a => ({ id: a.id })),
          components: (item.components || []).map(c => ({ id: c.id }))
      });

      const result = await this.entityManager.items.postOneOnline(clonedItem);
      
      this.goToDetails(result);

    } catch (error) {
        this.alertDialogService.openDialog({
            data: { messageLabel: 'ITEMS.SHOWCASE.COPY_ERROR' }
        });
    }
  }

  /**
   * Rimuove ricorsivamente ID e timestamp dall'oggetto
   */
  private recursiveCleanup(obj: any): void {
    const keysToDelete = [
        'id', 'updatedby_id', 'updated_at', 'createdby_id', 
        'created_at', 'item_id', 'variation_id', 'bom_component_id'
    ];

    keysToDelete.forEach(key => delete obj[key]);

    Object.keys(obj).forEach(key => {
        const value = obj[key];
        if (Array.isArray(value)) {
            value.forEach(item => typeof item === 'object' && this.recursiveCleanup(item));
        } else if (value !== null && typeof value === 'object') {
            this.recursiveCleanup(value);
        }
    });
  }

  deleteItems(ids: number[]) {
    const deletePromises = ids.map(id => this.entityManager.items.deleteOneOnline(id));

    Promise.all(deletePromises)
    .then((results) => {
      // Ricarica i dati della griglia
      if(this.isClientSide) {
        this.tableData[0].rowData$ = of(this.itemDataCatalogService.getDataByKey("items") || []).pipe(delay(100));
      } else {
        this.gridRef.gridApi?.refreshServerSide({ purge: true });
      }
      
      // Pulizia selezione visiva
      this.gridRef.gridApi?.deselectAll();
      this.isSelected.set(false);
    })
    .catch((error) => {
      console.error("Errore durante la cancellazione del prodotto: ", error);
    })
  }
  
  generateLabels(item: Items): void {
    this.itemLabelsDialogService.openDialog({
      data: {
        promotion: { 
          name: item.name, 
          base_pricelist: this.priceList,
          items: [],
          active: true
        },
        items: [structuredClone(item)] 
      }
    });
  }

  async generateBulkLabels() {
    const selectedRows = await this.getSelectedRows();

    if (selectedRows.length === 0) {
      return;
    }

    this.itemLabelsDialogService.openDialog({
      data: {
        promotion: { 
          name: "", 
          base_pricelist: this.priceList,
          items: [],
          active: true
        },
        items: structuredClone(selectedRows)
      }
    });
  }

  saveGridState() {
    if (this.gridRef && this.gridRef.gridApi) {
      this.gridStateService.saveState("items", {
        filterModel: this.gridRef.gridApi?.getFilterModel(),
        // columnState: this.gridRef.gridRef.columnApi.getColumnState(),
        paginationPage: this.gridRef.gridApi?.paginationGetCurrentPage(),
        searchBarValue: this.toolbarEventsService.searchBarValue$.value,
        searchBarcode: this.toolbarEventsService.searchBarcode$.value,
        customAgGridFilters: this.customAgGridFilters || null
      });
    }
  }

  @autobind
  private async openBarcodeAndSearch() {
    const barcode = await this.barcodeManagerService.openCameraBarcode();
    this.initBarcodeFilters(barcode);
  }

  onBarcodeScannedAppleMobile() {
    if (this.environmentInfoService.isAppleMobile()) {
      const barcodeInput = this.renderer.selectRootElement('#barcodeInput');
      barcodeInput.focus();
      barcodeInput.value = '';
    }
  }

  onBarcodeScanned(barcode: string) {
    if (document.querySelectorAll('.barcode-reader').length > 1) {
      return;
    }
    this.initBarcodeFilters(barcode);
  }

  onSidebarResized() {
    if (this.gridRef && this.gridRef.gridApi) {
      setTimeout(() => {
        this.gridRef.saveColumnState();
      }, 100); 
    }
  }

  handleSelectionChange(selected: boolean) {
    this.isSelected.set(selected);
    this.createToolbarButtons(); 
  }

  initBarcodeFilters(barcode: string) {
    this.toolbarEventsService.searchBarcode$.next(true);
    this.toolbarEventsService.searchBarValue$.next(barcode);
    this.toolbarEventsService.searchBarIcon$.next('');
    this.toolbarEventsService.searchBarIconFilled$.next('');
    this.toolbarEventsService.events.next({ type: 'toolbar.search_barcode', barcode: barcode });
  }

  resetSearchBar() {
    this.toolbarEventsService.searchBarcode$.next(false);
    this.toolbarEventsService.searchBarValue$.next('');
    this.toolbarEventsService.searchBarIcon$.next('tune');
    this.toolbarEventsService.searchBarIconFilled$.next('tune');
    this.toolbarEventsService.events.next({ type: 'toolbar.search', search: '' });
  }

  ngOnDestroy(): void {
    if (this.isMobile) {
      this.gridStateService.updateState("items", {
        showExtendedTable: this.showExtendedTable()
      });
    }
    // this.toolbarLayoutService.showModuleTitle$.next(true);

    if(this.unregisterGlobalTransition) this.unregisterGlobalTransition();
    if(this.unregisterOnShowTransition) this.unregisterOnShowTransition();

    this.resetSearchBar();

    this.toolbarEventsService.buttons$.next({ barButtons: [], panelButtons: [] });
  }
}
